import React, { useEffect, useState, useMemo } from "react";
import {
  GraphQLEnumType,
  GraphQLList,
  GraphQLNonNull,
  GraphQLObjectType,
  GraphQLScalarType,
  GraphQLUnionType,
} from "graphql";
import { getOperationDefinition } from "./operationUtils";

export interface SelectionTreeProps {
  schemaSDL: string;
  operationName: string;
  operationType: "mutation" | "query";
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

interface FieldInfo {
  name: string;
  type: any;
}

function countWithPrefix(selected: Set<string>, prefix: string): number {
  return Array.from(selected).filter((p) => p.startsWith(prefix)).length;
}

function clearWithPrefix(selected: Set<string>, prefix: string): Set<string> {
  return new Set(Array.from(selected).filter((p) => !p.startsWith(prefix)));
}

export default function SelectionTree({
  schemaSDL,
  operationName,
  operationType,
  selected,
  onChange,
}: SelectionTreeProps) {
  const [fields, setFields] = useState<FieldInfo[]>([]);
  const [returnType, setReturnType] = useState<string>("");

  useEffect(() => {
    const field = getOperationDefinition(
      schemaSDL,
      operationType,
      operationName,
    );
    let rtype: any = field.type;
    while (rtype instanceof GraphQLNonNull || rtype instanceof GraphQLList)
      rtype = rtype.ofType;
    setReturnType(String(field.type));
    if (rtype instanceof GraphQLUnionType) {
      // For Union types, create virtual fields for each possible type
      const unionTypes = rtype.getTypes();
      const fs = unionTypes.map((type: GraphQLObjectType) => ({
        name: `[${type.name}]`,
        type: type,
      }));
      setFields(fs);
    } else if (rtype instanceof GraphQLObjectType) {
      const fs = Object.keys(rtype.getFields()).map((k) => ({
        name: k,
        type: rtype.getFields()[k].type,
      }));
      setFields(fs);
    } else {
      setFields([]);
    }
  }, [schemaSDL, operationName, operationType]);

  const handleToggle = (path: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(path);
    else next.delete(path);
    onChange(next);
  };

  const handleToggleMany = (paths: string[], checked: boolean) => {
    const next = new Set(selected);
    paths.forEach((p) => {
      if (checked) next.add(p);
      else next.delete(p);
    });
    onChange(next);
  };

  const handleClearPrefix = (prefix: string) => {
    onChange(clearWithPrefix(selected, prefix));
  };

  const rootLeafPaths = fields
    .filter((f) => isScalarEnum(unwrap(f.type)))
    .map((f) => f.name);
  const rootAllLeafSelected =
    rootLeafPaths.length > 0 && rootLeafPaths.every((p) => selected.has(p));
  const rootSelectedCount = selected.size;
  const [expanded, setExpanded] = useState(false);

  const handleRootChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) handleToggleMany(rootLeafPaths, true);
    else onChange(clearWithPrefix(selected, ""));
  };

  return (
    <ul>
      <li>
        <label>
          <input
            type="checkbox"
            checked={rootAllLeafSelected}
            onChange={handleRootChange}
          />
          {operationName}: {returnType} ({rootSelectedCount})
        </label>
        {fields.length > 0 && (
          <button type="button" onClick={() => setExpanded(!expanded)}>
            {expanded ? "-" : "+"}
          </button>
        )}
        {expanded && (
          <ul>
            {fields.map((f) => (
              <FieldNode
                key={f.name}
                field={f}
                path={f.name}
                selected={selected}
                onToggle={handleToggle}
                onToggleMany={handleToggleMany}
                onClearPrefix={handleClearPrefix}
              />
            ))}
          </ul>
        )}
      </li>
    </ul>
  );
}

interface FieldNodeProps {
  field: FieldInfo;
  path: string;
  selected: Set<string>;
  onToggle: (path: string, checked: boolean) => void;
  onToggleMany: (paths: string[], checked: boolean) => void;
  onClearPrefix: (prefix: string) => void;
}

function FieldNode({
  field,
  path,
  selected,
  onToggle,
  onToggleMany,
  onClearPrefix,
}: FieldNodeProps) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState<FieldInfo[] | null>(null);

  const type = unwrap(field.type);
  const isLeaf = isScalarEnum(type);
  const isUnion = type instanceof GraphQLUnionType;

  const defaultChildren = useMemo(() => {
    if (isLeaf) return [];
    if (isUnion) {
      // For Union types, create children for each possible type
      const unionTypes = type.getTypes();
      return unionTypes.map((t: GraphQLObjectType) => ({
        name: `[${t.name}]`,
        type: t,
      }));
    }
    const fields = (type as GraphQLObjectType).getFields();
    return Object.keys(fields).map((k) => ({ name: k, type: fields[k].type }));
  }, [isLeaf, isUnion, type]);

  useEffect(() => {
    if (expanded && children === null && !isLeaf) {
      setChildren(defaultChildren);
    }
  }, [expanded, children, isLeaf, defaultChildren]);

  const currentChildren = children ?? defaultChildren;
  const leafPaths = currentChildren
    .filter((c) => isScalarEnum(unwrap(c.type)))
    .map((c) => `${path}.${c.name}`);
  const allLeafSelected =
    leafPaths.length > 0 && leafPaths.every((p) => selected.has(p));
  const checked = isLeaf ? selected.has(path) : allLeafSelected;
  const selectedCount = isLeaf
    ? selected.has(path)
      ? 1
      : 0
    : countWithPrefix(selected, `${path}.`);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (isLeaf) {
      onToggle(path, checked);
    } else {
      if (checked) onToggleMany(leafPaths, true);
      else onClearPrefix(`${path}.`);
    }
  };

  return (
    <li>
      <label>
        <input type="checkbox" checked={checked} onChange={handleChange} />
        {isLeaf
          ? `${field.name}: ${String(field.type)}`
          : `${field.name}: ${String(field.type)} (${selectedCount})`}
      </label>
      {!isLeaf && (
        <button type="button" onClick={() => setExpanded(!expanded)}>
          {expanded ? "-" : "+"}
        </button>
      )}
      {expanded && children && (
        <ul>
          {children.map((c) => (
            <FieldNode
              key={c.name}
              field={c}
              path={`${path}.${c.name}`}
              selected={selected}
              onToggle={onToggle}
              onToggleMany={onToggleMany}
              onClearPrefix={onClearPrefix}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function unwrap(t: any): any {
  while (t instanceof GraphQLNonNull || t instanceof GraphQLList) t = t.ofType;
  return t;
}

function isScalarEnum(t: any): boolean {
  return t instanceof GraphQLScalarType || t instanceof GraphQLEnumType;
}
